import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  IconButton, 
  Menu, 
  MenuItem, 
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const drawerContent = (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="h6">Menu</Typography>
      </Box>
      <List>
        <ListItem button component={RouterLink} to="/">
          <ListItemIcon>
            <DashboardIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItem>
        <ListItem button component={RouterLink} to="/upload">
          <ListItemIcon>
            <CloudUploadIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="Upload" />
        </ListItem>
        <ListItem button component={RouterLink} to="/jobs">
          <ListItemIcon>
            <ListAltIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="Jobs" />
        </ListItem>
      </List>
      <Divider />
      <List>
        <ListItem sx={{ opacity: 0.7 }}>
          <ListItemText 
            primary={user?.name || "User"} 
            secondary={user?.email || user?.username}
            primaryTypographyProps={{ 
              noWrap: true, 
              style: { maxWidth: '220px' } 
            }}
            secondaryTypographyProps={{ 
              noWrap: true, 
              style: { maxWidth: '220px' } 
            }}
          />
        </ListItem>
        <ListItem button onClick={logout}>
          <ListItemIcon>
            <LogoutIcon color="error" />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <AppBar
      position="static"
      color="default"
      elevation={0}
      sx={{
        borderRadius: 0,
        backgroundColor: '#ffffff',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      }}
    >
      <Toolbar>
        {isMobile && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer(true)}
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Box
          component={RouterLink}
          to="/"
          sx={{ display: 'flex', alignItems: 'center', mr: { xs: 1, sm: 3 }, flexShrink: 0 }}
        >
          <Box
            component="img"
            src="/magna-logo-official-dark.svg"
            alt="Magna Legal Services"
            sx={{ height: { xs: 26, sm: 36 }, maxWidth: { xs: '120px', sm: '170px' } }}
          />
        </Box>

        <Typography
          variant={isMobile ? "h6" : "h5"}
          component="div"
          color="primary"
          sx={{
            flexGrow: 1,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }
          }}
        >
          Deposition Summaries
        </Typography>

        {!isMobile ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              color="inherit"
              component={RouterLink}
              to="/"
              startIcon={<DashboardIcon />}
              sx={{ color: 'text.primary' }}
            >
              Dashboard
            </Button>

            <Button
              color="inherit"
              component={RouterLink}
              to="/upload"
              startIcon={<CloudUploadIcon />}
              sx={{ color: 'text.primary' }}
            >
              Upload
            </Button>

            <Button
              color="inherit"
              component={RouterLink}
              to="/jobs"
              startIcon={<ListAltIcon />}
              sx={{ color: 'text.primary' }}
            >
              Jobs
            </Button>

            <IconButton
              size="large"
              onClick={handleMenu}
              color="inherit"
              sx={{ color: 'text.primary' }}
            >
              {user?.picture ? (
                <Avatar 
                  alt={user.name} 
                  src={user.picture} 
                  sx={{ width: 32, height: 32 }}
                />
              ) : (
                <AccountCircleIcon sx={{ width: 32, height: 32 }} />
              )}
            </IconButton>
            
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem disabled>
                {user?.email || user?.username}
              </MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        ) : (
          <IconButton
            size="large"
            onClick={handleMenu}
            color="inherit"
            sx={{ color: 'text.primary' }}
          >
            {user?.picture ? (
              <Avatar 
                alt={user.name} 
                src={user.picture} 
                sx={{ width: 32, height: 32 }}
              />
            ) : (
              <AccountCircleIcon sx={{ width: 32, height: 32 }} />
            )}
          </IconButton>
        )}

        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={toggleDrawer(false)}
        >
          {drawerContent}
        </Drawer>
        
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem disabled>
            {user?.email || user?.username}
          </MenuItem>
          <MenuItem onClick={handleLogout}>Logout</MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;